import type {
  OpeningRetryPrefill,
  PoolTokenPermissionsResponse,
  RecenterQuoteResponse,
} from "@auto-pools/core";
import {
  defaultTargetMint,
  decimal,
  formatNative,
  invertPrice as invertExactPrice,
  nativeUnits,
  RANGE_WIDTHS,
  stopDistances,
  type OpenPositionRequest,
} from "@auto-pools/core";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { api, ApiError } from "../api/client";
import { useRuntimeStatus } from "../api/runtimeStatusContext";
import { AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON } from "../components/AutopilotProvider";
import { useAutopilot } from "../components/autopilotContext";
import { useViewer } from "../auth/authContext";
import {
  DEFAULT_ESTIMATED_YIELD_PERIOD,
  ESTIMATED_YIELD_PERIODS,
  EstimatedYieldRow,
} from "../components/EstimatedYieldRow";
import { MarketChart } from "../components/MarketChart";
import { PriceScale } from "../components/PriceScale";
import { RefreshButton } from "../components/RefreshButton";
import { ScaledPoolUnitsNote } from "../components/ScaledPoolUnits";
import { TokenPermissions } from "../components/TokenPermissions";
import { useRefreshPhase } from "../lib/useRefreshPhase";
import { routedQuoteLines } from "../lib/operationPresentation";
import { TokenIcon, TokenPair, TokenPairName } from "../components/TokenPair";
import { DexBadge } from "../components/DexBadge";
import { dexName, dexPoolUrl, openableReason } from "../lib/dex";
import { priceFrame, ruleInverted, type PriceFrame } from "../lib/priceFrame";
import { StopDistanceControl } from "../components/StopDistanceControl";
import type { StopDistanceRowView } from "../components/StopDistanceControl";
import { buildStopRowView, mapStopPrefill, stopPairText } from "../lib/stopPolicy";
import {
  formatPrice,
  formatPercent,
  formatFundingUsd,
  formatFundingToken,
  formatToken,
  shorten,
} from "../lib/format";
import type {
  EstimatedYieldResponse,
  Operation,
  Pool,
  Quote,
  QuoteRequest,
  QuoteRequestBody,
  RangeDirection,
  RangeEntryMode,
  Token,
  YieldPeriod,
} from "../types";
import {
  convertFunding,
  fundingProblem,
  evaluateFunding,
  fundingStale,
  validateFundingSnapshot,
} from "./funding";
import type { FundingSnapshot } from "@auto-pools/core";
import type { OpenComposition } from "@auto-pools/core";
import { preliminaryRange, preliminaryStops } from "./geometry";
import { PositionPreview, useOpenPreview } from "./PositionPreview";
import { AcceptedOperation } from "./AcceptedOperation";

const OFFSETS = [3, 5, 10, 15] as const;
/** Strictly inside the displayed bounds; both sides flip together when the display is inverted. */
function straddles(range: { min: string; max: string }, reference: string) {
  try {
    const price = decimal(reference);
    return decimal(range.min).compare(price) < 0 && price.compare(decimal(range.max)) < 0;
  } catch {
    return false;
  }
}
/**
 * The estimated yield row keeps its own stored preference: the catalog's `yieldPeriod` defaults to
 * 365d while this row defaults to 24h, so the two must never share a key.
 */
const YIELD_PERIOD_STORAGE_KEY = "auto-pools:estimated-yield-period:v1";
function readYieldPeriod(): YieldPeriod {
  try {
    const stored = sessionStorage.getItem(YIELD_PERIOD_STORAGE_KEY);
    return (
      ESTIMATED_YIELD_PERIODS.find((period) => period === stored) ?? DEFAULT_ESTIMATED_YIELD_PERIOD
    );
  } catch {
    return DEFAULT_ESTIMATED_YIELD_PERIOD;
  }
}
function writeYieldPeriod(period: YieldPeriod) {
  try {
    sessionStorage.setItem(YIELD_PERIOD_STORAGE_KEY, period);
  } catch {
    // The period preference is optional when browser storage is unavailable.
  }
}
function preferredTarget(pool: Pool) {
  return defaultTargetMint(pool.tokenA.mint, pool.tokenB.mint) ?? "";
}

const STOP_PREFILL_DEFAULT_NOTICE =
  "The previous setup had no automatic exit; both stops are now added.";

/**
 * The wizard's Auto-exit rows for a "Set up again" prefill: a disabled or malformed stored policy
 * falls back to an equal 5/5 pair, a legacy one-sided policy backfills the missing side at the same
 * distance, and everything else maps the stored canonical distances into the display frame.
 */
function wizardStopPrefill(prefill: OpeningRetryPrefill | undefined) {
  const fallback = { below: "5", above: "5", mirror: true, notice: STOP_PREFILL_DEFAULT_NOTICE };
  if (!prefill) return { ...fallback, notice: null };
  const policy = prefill.request.exitPolicy;
  if (!policy.enabled) return fallback;
  try {
    return mapStopPrefill(stopDistances(policy), prefill.inverted) ?? fallback;
  } catch {
    return fallback;
  }
}

function CompositionMinimums({
  composition: c,
  pool,
}: {
  composition: OpenComposition;
  pool: Pool;
}) {
  const hasGuard =
    c.minimumLiquidity !== undefined ||
    c.minimumSqrtPrice !== undefined ||
    c.maximumSqrtPrice !== undefined;
  const hasAmounts = c.minimumDepositA !== undefined || c.minimumDepositB !== undefined;
  if (!hasGuard && !hasAmounts && c.basis !== "simulated-post-swap-price") return null;
  const amount = (value: OpenComposition["minimumDepositA"], fallback: Token) => {
    if (!value) return "Unavailable";
    const token = [pool.tokenA, pool.tokenB].find((item) => item.mint === value.mint) ?? fallback;
    return `${formatToken(value.decimal, "long")} ${token.symbol}${
      value.usd === null ? " · USD unavailable" : ` · ${formatFundingUsd(value.usd)}`
    }`;
  };
  return (
    <section className="composition-minimums" aria-label="Lower composition estimate">
      <h4>Lower composition estimate for this quote</h4>
      <p>
        {c.basis === "simulated-post-swap-price"
          ? "Expected composition uses the simulated post-swap price."
          : "Expected composition uses the observed pool price."}{" "}
        These lower composition amounts belong to this quote. They are not a minimum funding
        requirement or a fixed USD entry limit from Orca. Expected deposits and unused amounts shown
        above remain indicative.
      </p>
      {hasAmounts ? (
        <dl>
          <div>
            <dt>Lower {pool.tokenA.symbol} composition estimate</dt>
            <dd>{amount(c.minimumDepositA, pool.tokenA)}</dd>
          </div>
          <div>
            <dt>Lower {pool.tokenB.symbol} composition estimate</dt>
            <dd>{amount(c.minimumDepositB, pool.tokenB)}</dd>
          </div>
          <div>
            <dt>Lower total composition estimate</dt>
            <dd>{formatFundingUsd(c.minimumTotalDepositUsd ?? null)}</dd>
          </div>
        </dl>
      ) : (
        <p>
          The server enforces minimum liquidity across the approved price guard. Token and USD
          minima are unavailable for this quote.
        </p>
      )}
      {hasGuard && (
        <small>
          Opening stops if the simulated pool price leaves the approved guard or the protected
          minimum liquidity cannot be met.
        </small>
      )}
    </section>
  );
}

export function OpenWizard({
  pool,
  onClose,
  onPositions,
  referenceAt,
  prefill,
}: {
  prefill?: OpeningRetryPrefill;
  pool: Pool;
  onClose: () => void;
  onPositions?: () => void;
  referenceAt?: string;
}) {
  const [recenter, setRecenter] = useState(false);
  const [recenterSlippage, setRecenterSlippage] = useState("");
  let recenterBps: number | undefined;
  let recenterValid = true;
  try {
    if (recenterSlippage !== "") {
      recenterBps = Number(nativeUnits(recenterSlippage, 2));
      recenterValid = recenterBps <= 500;
    }
  } catch {
    recenterValid = false;
  }
  const [reviewShown, setReviewShown] = useState(false);
  const [settingsShown, setSettingsShown] = useState(false);
  const reviewButton = useRef<HTMLButtonElement>(null);
  const settingsButton = useRef<HTMLButtonElement>(null);
  const panelHeading = useRef<HTMLHeadingElement>(null);
  const [settingsTarget, setSettingsTarget] = useState<HTMLElement | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [inverted, setInverted] = useState(
    () => prefill?.inverted ?? ruleInverted(pool.tokenA, pool.tokenB),
  );
  const [rangeMode, setRangeMode] = useState<RangeEntryMode>(prefill ? "manual" : "percent");
  const [width, setWidth] = useState(20);
  const [direction, setDirection] = useState<RangeDirection>("balanced");
  const [short, setShort] = useState(3);
  const [manual, setManual] = useState<{
    min: string;
    max: string;
    inverted: boolean;
    canonical: { min: string; max: string };
    valid: boolean;
  } | null>(
    prefill
      ? {
          min: prefill.inverted
            ? invertExactPrice(prefill.request.upperPrice)
            : prefill.request.lowerPrice,
          max: prefill.inverted
            ? invertExactPrice(prefill.request.lowerPrice)
            : prefill.request.upperPrice,
          inverted: prefill.inverted,
          canonical: { min: prefill.request.lowerPrice, max: prefill.request.upperPrice },
          valid: true,
        }
      : null,
  );
  const manualMin = !manual
    ? ""
    : manual.inverted === inverted
      ? manual.min
      : manual.valid
        ? invertExactPrice(manual.max)
        : "";
  const manualMax = !manual
    ? ""
    : manual.inverted === inverted
      ? manual.max
      : manual.valid
        ? invertExactPrice(manual.min)
        : "";
  const [fundingMint, setFundingMint] = useState(prefill?.request.fundingMint ?? pool.tokenA.mint);
  const [inputAmount, setInputAmount] = useState(
    prefill
      ? formatNative(
          BigInt(prefill.request.amountNative),
          prefill.request.fundingMint === pool.tokenA.mint
            ? pool.tokenA.decimals
            : pool.tokenB.decimals,
        )
      : "",
  );
  const [currency, setCurrency] = useState<"token" | "usd">("token");
  const [funding, setFunding] = useState<FundingSnapshot | null>(null);
  const [permissionResponse, setPermissionResponse] = useState<{
    poolAddress: string;
    value: PoolTokenPermissionsResponse;
  } | null>(null);
  const [fundingError, setFundingError] = useState<string | null>(null);
  const [fundingPending, setFundingPending] = useState(false);
  const [displayCurrency, setDisplayCurrency] = useState<"token" | "usd">("token");
  const [now, setNow] = useState(Date.now);
  const heading = useRef<HTMLHeadingElement>(null);
  const quoteGeneration = useRef(0);

  const [autoSwap, setAutoSwap] = useState(prefill?.request.autoSwap ?? true);
  const [slippage, setSlippage] = useState(
    prefill ? formatNative(BigInt(prefill.request.caps.maxSlippageBps), 2) : "0.5",
  );
  const [priceImpact, setPriceImpact] = useState(
    prefill ? formatNative(BigInt(prefill.request.caps.maxPriceImpactBps), 2) : "1",
  );
  const [priorityFee, setPriorityFee] = useState(
    prefill ? formatNative(BigInt(prefill.request.caps.maxPriorityFeeLamports), 9) : "0.001",
  );
  const [networkFee, setNetworkFee] = useState(
    prefill ? formatNative(BigInt(prefill.request.caps.maxNetworkFeeLamports), 9) : "0.01",
  );
  const [rentCap, setRentCap] = useState(
    prefill
      ? formatNative(BigInt(prefill.request.caps.maxRentLamports), 9)
      : // A Raydium open may also create up to two shared tick arrays nobody refunds (0.072 SOL
        // each) plus a transient WSOL account; same cap as the autopilot's Raydium opens.
        pool.dex === "raydium"
        ? "0.16"
        : "0.03",
  );
  const [solReserve, setSolReserve] = useState(
    prefill ? formatNative(BigInt(prefill.request.caps.minSolReserveLamports), 9) : "0.02",
  );
  const [maxAttempts, setMaxAttempts] = useState(prefill?.request.caps.maxAttempts ?? 4);
  const [stopPrefill] = useState(() => wizardStopPrefill(prefill));
  const [stopBelow, setStopBelow] = useState(stopPrefill.below);
  const [stopAbove, setStopAbove] = useState(stopPrefill.above);
  const [stopMirror, setStopMirror] = useState(stopPrefill.mirror);
  const [stopInvalid, setStopInvalid] = useState(false);
  const [targetMint, setTargetMint] = useState(
    prefill?.request.exitPolicy.targetMint ?? preferredTarget(pool),
  );
  const [prepared, setPrepared] = useState<{
    quote: Quote;
    request: QuoteRequest;
    funding: FundingSnapshot;
    amount: string;
    usd: string;
  } | null>(null);
  // A lost POST response can still mean acceptance. Pin this binding beyond finally
  // and modal dismissal so the mount's idempotency key can never be paired with Q2.
  const submitted = useRef<{ prepared: NonNullable<typeof prepared>; identity: string } | null>(
    null,
  );
  const quote = prepared?.quote ?? null;
  const [adjustment, setAdjustment] = useState<string | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quotePending, setQuotePending] = useState(false);
  const openInFlight = useRef(false);
  const [operation, setOperation] = useState<Operation | null>(null);
  const [openPending, setOpenPending] = useState(false);
  const [reviewHeaderTarget, setReviewHeaderTarget] = useState<HTMLElement | null>(null);
  const [reviewActionTarget, setReviewActionTarget] = useState<HTMLElement | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const runtime = useRuntimeStatus();
  const autopilot = useAutopilot();
  // A viewer browses the whole wizard, preview and quote included; opening is a command.
  const viewer = useViewer();

  const walletIdentity = JSON.stringify(runtime.data?.wallet ?? null);
  const identity = JSON.stringify([walletIdentity, pool]);
  const activeIdentity = useRef(identity);
  activeIdentity.current = identity;
  const snapshotRef = useRef<FundingSnapshot | null>(null);
  const pendingRead = useRef<{
    identity: string;
    forced: boolean;
    controller: AbortController;
    promise: Promise<FundingSnapshot>;
  } | null>(null);
  const quoteController = useRef<AbortController | null>(null);
  function invalidate() {
    if (submitted.current || operation) return;
    quoteGeneration.current++;
    quoteController.current?.abort();
    setQuotePending(false);
    setPrepared(null);
    setReviewShown(false);
    setAdjustment(null);
    setQuoteError(null);
  }
  function cancel() {
    invalidate();
    pendingRead.current?.controller.abort();
    pendingRead.current = null;
    setFundingPending(false);
  }
  function close() {
    setLeaving(true);
    cancel();
    onClose();
  }
  const readFunding = useCallback(
    async (forced = false, generation?: number): Promise<FundingSnapshot> => {
      const check = () => {
        if (
          activeIdentity.current !== identity ||
          (generation !== undefined && quoteGeneration.current !== generation)
        )
          throw new DOMException("Cancelled", "AbortError");
      };
      check();
      const current = pendingRead.current;
      if (current?.identity === identity) {
        const result = await current.promise;
        check();
        if (!forced || current.forced) return result;
        // A forced caller waits out a cacheable read, then joins/starts exactly one forced read.
        return readFunding(true, generation);
      }
      const controller = new AbortController();
      setFundingPending(true);
      setFundingError(null);
      const promise = api<FundingSnapshot>(
        `/pools/${pool.address}/funding-snapshot${forced ? "?refresh=true" : ""}`,
        { signal: controller.signal, sensitive: true },
      )
        .then((result) => {
          if (controller.signal.aborted || activeIdentity.current !== identity)
            throw new DOMException("Cancelled", "AbortError");
          validateFundingSnapshot(
            result,
            pool,
            JSON.parse(walletIdentity) as { address?: string; generation?: string } | null,
          );
          snapshotRef.current = result;
          setFunding(result);
          setNow(Date.now());
          return result;
        })
        .catch((reason: unknown) => {
          if (!controller.signal.aborted && activeIdentity.current === identity)
            setFundingError(reason instanceof Error ? reason.message : "Funding unavailable");
          throw reason;
        })
        .finally(() => {
          if (pendingRead.current?.controller === controller) {
            pendingRead.current = null;
            setFundingPending(false);
          }
        });
      pendingRead.current = { identity, forced, controller, promise };
      const result = await promise;
      check();
      return result;
    },
    [identity, walletIdentity, pool],
  );
  useEffect(() => {
    const pendingQuote = quoteGeneration;
    heading.current?.focus();
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      pendingQuote.current++;
      pendingRead.current?.controller.abort();
      quoteController.current?.abort();
      window.clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    quoteGeneration.current++;
    quoteController.current?.abort();
    pendingRead.current?.controller.abort();
    pendingRead.current = null;
    snapshotRef.current = null;
    setFunding(null);
    if (!submitted.current) {
      setPrepared(null);
      setReviewShown(false);
    }
    setAdjustment(null);
    setQuotePending(false);
    setFundingPending(false);
    setFundingError(null);
  }, [identity]);
  useEffect(() => {
    if (recenter && !submitted.current) {
      quoteGeneration.current++;
      quoteController.current?.abort();
      setQuotePending(false);
      setPrepared(null);
      setReviewShown(false);
    }
  }, [recenter, runtime.executionReady]);
  const entryAttempt = useRef("");
  useEffect(() => {
    const entry = identity;
    if (entryAttempt.current === entry) return;
    entryAttempt.current = entry;
    if (
      !submitted.current &&
      (JSON.parse(walletIdentity) as { configured?: boolean } | null)?.configured &&
      fundingStale(snapshotRef.current)
    )
      void readFunding().catch(() => undefined);
  }, [identity, walletIdentity, readFunding]);
  const selectedFunding = pool.tokenA.mint === fundingMint ? pool.tokenA : pool.tokenB;
  const priceUsd = funding?.assets.find((asset) => asset.mint === fundingMint)?.priceUsd ?? null;
  let amount = "",
    usdAmount = "";
  try {
    ({ token: amount, usd: usdAmount } = convertFunding(
      inputAmount,
      currency,
      priceUsd,
      selectedFunding.decimals,
    ));
  } catch {
    /* Invalid text stays editable. */
  }
  const setAmount = (value: string) => {
    setCurrency("token");
    setInputAmount(value);
    invalidate();
  };
  const evaluation = evaluateFunding(
    funding,
    fundingMint,
    amount || (currency === "usd" && fundingStale(funding) ? inputAmount : ""),
    networkFee,
    rentCap,
    solReserve,
    now,
  );
  const balanceProblem = evaluation.message;
  function refreshFunding() {
    invalidate();
    void readFunding(true, quoteGeneration.current).catch(() => undefined);
  }
  function completedEdit() {
    if (fundingValid && fundingStale(snapshotRef.current))
      void readFunding(false, quoteGeneration.current).catch(() => undefined);
  }

  const frame = useMemo(() => priceFrame(pool.tokenA, pool.tokenB, inverted), [pool, inverted]);
  const onPermissionResponse = useCallback(
    (value: PoolTokenPermissionsResponse | null) => {
      setPermissionResponse(value ? { poolAddress: pool.address, value } : null);
    },
    [pool.address],
  );
  const scaledTokens =
    permissionResponse?.poolAddress === pool.address
      ? permissionResponse.value.tokens
      : (funding?.assets ?? [pool.tokenA, pool.tokenB]);
  const first = frame.base;
  const second = frame.quote;
  const canonicalReference =
    prepared?.request.referencePrice ?? funding?.poolPrice.price ?? pool.price;
  const reference = useMemo(() => {
    if (!canonicalReference) return "";
    try {
      return inverted ? invertExactPrice(canonicalReference) : canonicalReference;
    } catch {
      return "";
    }
  }, [canonicalReference, inverted]);
  const preview = preliminaryRange(
    reference,
    rangeMode,
    width,
    direction,
    short,
    manualMin,
    manualMax,
  );
  const stopBasisInverted = prefill?.request.exitPolicy.enabled
    ? prefill.request.exitPolicy.thresholdInverted === true
    : inverted;
  const previewStops = preliminaryStops(
    preview.min,
    preview.max,
    stopBelow,
    stopAbove,
    stopBasisInverted !== inverted,
  );
  const stopBlocked = stopInvalid || previewStops.error !== undefined;
  const chartLower =
    reviewShown && quote
      ? inverted
        ? invertExactPrice(quote.upperPrice)
        : quote.lowerPrice
      : preview.min;
  const chartUpper =
    reviewShown && quote
      ? inverted
        ? invertExactPrice(quote.lowerPrice)
        : quote.upperPrice
      : preview.max;
  const chartLowerStop =
    reviewShown && quote
      ? inverted
        ? quote.exitPolicy.upperTrigger && invertExactPrice(quote.exitPolicy.upperTrigger)
        : quote.exitPolicy.lowerTrigger
      : previewStops.lower;
  const chartUpperStop =
    reviewShown && quote
      ? inverted
        ? quote.exitPolicy.lowerTrigger && invertExactPrice(quote.exitPolicy.lowerTrigger)
        : quote.exitPolicy.upperTrigger
      : previewStops.upper;
  const stopQuotePriceUsd =
    funding?.assets.find((asset) => asset.mint === second.mint)?.priceUsd ?? null;
  const stopUsdReason =
    stopQuotePriceUsd === null ? `USD price of ${second.symbol} unavailable` : undefined;
  const stopRows = {
    below: buildStopRowView(previewStops.lower, reference, stopQuotePriceUsd),
    above: buildStopRowView(previewStops.upper, reference, stopQuotePriceUsd),
  };
  const shortValid = direction === "balanced" || short <= width / 2;
  const rangeShapeValid =
    Number(preview.min) > 0 &&
    Number(preview.max) > Number(preview.min) &&
    shortValid &&
    (rangeMode !== "manual" || !!manual?.valid);
  // Raydium refuses an open whose range leaves out the current price (RANGE_NOT_AROUND_PRICE). The
  // presets always straddle it; manual bounds may not. The server's tick check stays authoritative.
  const outsidePrice = pool.dex === "raydium" && rangeShapeValid && !straddles(preview, reference);
  const rangeValid = rangeShapeValid && !outsidePrice;
  let limitsValid = false;
  try {
    const slippageBps = nativeUnits(slippage, 2);
    const impactBps = nativeUnits(priceImpact, 2);
    limitsValid =
      slippageBps >= 0n &&
      slippageBps < 10_000n &&
      impactBps >= 0n &&
      impactBps < 10_000n &&
      nativeUnits(priorityFee, 9) >= 0n &&
      nativeUnits(networkFee, 9) > 0n &&
      nativeUnits(rentCap, 9) >= 0n &&
      nativeUnits(solReserve, 9) >= 0n &&
      Number.isSafeInteger(maxAttempts) &&
      maxAttempts > 0 &&
      maxAttempts <= 20;
  } catch {
    limitsValid = false;
  }
  let fundingValid = false;
  try {
    fundingValid =
      limitsValid &&
      (evaluation.kind === "valid" || evaluation.kind === "refresh") &&
      (currency === "usd"
        ? decimal(inputAmount).units > 0n
        : nativeUnits(inputAmount, selectedFunding.decimals) > 0n);
  } catch {
    fundingValid = false;
  }
  const unit = frame.unit;

  function editManual(min: string, max: string) {
    const bound = (value: string, previous: string, canonical: string | undefined) => {
      if (value === previous && canonical !== undefined) return canonical;
      try {
        if (decimal(value).units > 0n) return inverted ? invertExactPrice(value) : value;
      } catch {
        /* An incomplete edited bound has no canonical price. */
      }
      return "";
    };
    // Only the edited display bound may replace its corresponding exact canonical bound.
    const lower = bound(min, manualMin, inverted ? manual?.canonical.max : manual?.canonical.min);
    const upper = bound(max, manualMax, inverted ? manual?.canonical.min : manual?.canonical.max);
    const canonical = inverted ? { min: upper, max: lower } : { min: lower, max: upper };
    let valid = false;
    try {
      valid = decimal(min).units > 0n && decimal(min).compare(decimal(max)) < 0;
    } catch {
      /* Keep incomplete edits invalid. */
    }
    setManual({ min, max, inverted, canonical, valid });
    invalidate();
  }
  function toggleOrientation() {
    if (rangeMode === "manual" && !manual?.valid) return;
    setInverted((value) => !value);
    invalidate();
  }
  /**
   * Canonical bounds (token B per token A) for a displayed range. The display can be inverted, and
   * sending displayed bounds would make the server snap the reciprocal range; every caller — the
   * quote path and the estimated yield row — sends what this returns.
   */
  function canonicalBounds(range: { min: string; max: string }) {
    if (rangeMode === "manual")
      return { lower: manual?.canonical.min ?? "", upper: manual?.canonical.max ?? "" };
    return inverted
      ? { lower: invertExactPrice(range.max), upper: invertExactPrice(range.min) }
      : { lower: range.min, upper: range.max };
  }
  function convertedAt(snapshot: FundingSnapshot) {
    return convertFunding(
      inputAmount,
      currency,
      snapshot.assets.find((a) => a.mint === fundingMint)?.priceUsd ?? null,
      selectedFunding.decimals,
    );
  }
  function requestBody(snapshot: FundingSnapshot): QuoteRequestBody {
    const converted = convertedAt(snapshot);
    const problem = fundingProblem(
      snapshot,
      fundingMint,
      converted.token,
      networkFee,
      rentCap,
      solReserve,
    );
    if (problem) throw new Error(problem);
    const freshReference = inverted
      ? invertExactPrice(snapshot.poolPrice.price)
      : snapshot.poolPrice.price;
    const range = preliminaryRange(
      freshReference,
      rangeMode,
      width,
      direction,
      short,
      manualMin,
      manualMax,
    );
    if (rangeMode === "manual" && !manual?.valid) throw new Error("Complete both manual bounds");
    const { lower: canonicalLower, upper: canonicalUpper } = canonicalBounds(range);
    // The display frame may be inverted; canonical lower/upper are the reciprocal of below/above.
    const canonicalLowerThreshold = inverted ? stopAbove : stopBelow;
    const canonicalUpperThreshold = inverted ? stopBelow : stopAbove;
    return {
      poolAddress: pool.address,
      referencePrice: snapshot.poolPrice.price,
      lowerPrice: canonicalLower,
      upperPrice: canonicalUpper,
      fundingMint,
      amountNative: nativeUnits(converted.token, selectedFunding.decimals).toString(),
      autoSwap,
      exitPolicy: {
        enabled: true,
        side: "both",
        lowerThresholdPercent: canonicalLowerThreshold,
        upperThresholdPercent: canonicalUpperThreshold,
        ...(stopBasisInverted ? { thresholdInverted: true } : {}),
        targetMint,
      },
      caps: {
        maxInputNative:
          prefill?.request.caps.maxInputNative ??
          nativeUnits(converted.token, selectedFunding.decimals).toString(),
        maxNetworkFeeLamports: nativeUnits(networkFee, 9).toString(),
        maxPriorityFeeLamports: nativeUnits(priorityFee, 9).toString(),
        maxRentLamports: nativeUnits(rentCap, 9).toString(),
        minSolReserveLamports: nativeUnits(solReserve, 9).toString(),
        maxSlippageBps: Number(nativeUnits(slippage, 2)),
        maxPriceImpactBps: Number(nativeUnits(priceImpact, 2)),
        maxAttempts,
      },
    };
  }

  const previewDisplayContext = JSON.stringify([
    identity,
    funding?.wallet,
    quoteGeneration.current,
    inputAmount,
    currency,
    fundingMint,
    rangeMode,
    width,
    direction,
    short,
    manual,
    inverted,
    autoSwap,
    slippage,
    priceImpact,
    priorityFee,
    networkFee,
    rentCap,
    solReserve,
    maxAttempts,
    stopBelow,
    stopAbove,
    targetMint,
  ]);
  const previewContext = JSON.stringify([previewDisplayContext, funding?.observedAt]);
  const positionPreview = useOpenPreview(
    previewContext,
    previewDisplayContext,
    fundingValid && rangeValid && !stopBlocked,
    !recenter && !reviewShown && !quotePending && !submitted.current && !operation && !leaving,
    now,
    pool,
    async (forced) => {
      if (forced || fundingStale(snapshotRef.current)) {
        await readFunding(forced, quoteGeneration.current);
        // Refresh changes the preview context; prepare again with the current render's binding.
        return null;
      }
      const snapshot = snapshotRef.current;
      if (!snapshot) throw new Error("Funding unavailable");
      return { request: requestBody(snapshot), wallet: snapshot.wallet };
    },
  );

  // The estimated yield row is informational: it never gates the form and a failure of its request
  // never becomes a form error. The browser performs no arithmetic on the figure it renders.
  const [yieldPeriod, setYieldPeriod] = useState<YieldPeriod>(readYieldPeriod);
  const [yieldEstimate, setYieldEstimate] = useState<EstimatedYieldResponse | null>(null);
  const [yieldPending, setYieldPending] = useState(false);
  const [yieldFailed, setYieldFailed] = useState(false);
  let yieldLower: string | null = null,
    yieldUpper: string | null = null;
  if (rangeValid) {
    try {
      const bounds = canonicalBounds(preview);
      if (
        bounds.lower &&
        bounds.upper &&
        decimal(bounds.lower).units > 0n &&
        decimal(bounds.lower).compare(decimal(bounds.upper)) < 0
      ) {
        yieldLower = bounds.lower;
        yieldUpper = bounds.upper;
      }
    } catch {
      /* An incomplete range has no canonical bounds to estimate. */
    }
  }
  // `displayResult` is only defined while the preview matches the current range and amount, so the
  // deposit refinement can never be attributed to a range the operator has already changed.
  const depositLiquidity = positionPreview.displayResult?.composition.liquidityDelta;
  const yieldActive = !recenter && !operation && !leaving;
  const poolAddress = pool.address;
  useEffect(() => {
    const lower = yieldLower,
      upper = yieldUpper;
    if (!yieldActive || lower === null || upper === null) return;
    const controller = new AbortController();
    // The effect is keyed on `depositLiquidity` itself: the preview lands asynchronously after the
    // amount is typed, so watching only the edits would leave the marginal figure in place.
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams({
        period: yieldPeriod,
        lowerPrice: lower,
        upperPrice: upper,
      });
      if (depositLiquidity !== undefined) query.set("depositLiquidity", depositLiquidity);
      setYieldPending(true);
      void api<EstimatedYieldResponse>(`/pools/${poolAddress}/yield-estimate?${query.toString()}`, {
        signal: controller.signal,
      })
        .then((result) => {
          if (controller.signal.aborted) return;
          setYieldEstimate(result);
          setYieldFailed(false);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          // Keeping the previous number would present an old range's return as the current one.
          setYieldEstimate(null);
          setYieldFailed(true);
        })
        .finally(() => {
          if (!controller.signal.aborted) setYieldPending(false);
        });
    }, 400);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
      setYieldPending(false);
    };
  }, [poolAddress, yieldPeriod, yieldLower, yieldUpper, depositLiquidity, yieldActive]);

  async function getQuote(renew = false) {
    if (
      submitted.current ||
      operation ||
      openInFlight.current ||
      quotePending ||
      leaving ||
      (recenter
        ? !prefill?.sourceOperationId || !recenterValid
        : !rangeValid ||
          !fundingValid ||
          !targetMint ||
          stopBlocked ||
          (!renew && (!positionPreview.isFresh() || fundingStale(snapshotRef.current))))
    )
      return;
    const generation = ++quoteGeneration.current;
    const controller = new AbortController();
    quoteController.current = controller;
    const current = () => quoteGeneration.current === generation && !controller.signal.aborted;
    setQuotePending(true);
    setReviewShown(false);
    setQuoteError(null);
    setPrepared(null);
    setAdjustment(null);
    let caps: QuoteRequest["caps"] | undefined;
    try {
      if (recenter && prefill?.sourceOperationId) {
        const snapshot = snapshotRef.current ?? (await readFunding(true, generation));
        if (!current()) return;
        const result = await api<RecenterQuoteResponse>(
          `/operations/${encodeURIComponent(prefill.sourceOperationId)}/recenter-quote`,
          {
            method: "POST",
            body: recenterBps === undefined ? {} : { maxSlippageBps: recenterBps },
            signal: controller.signal,
          },
        );
        if (!current()) return;
        if (
          !result.quote.recenterReview ||
          result.prefill.sourceOperationId !== prefill.sourceOperationId ||
          result.prefill.request.poolAddress !== pool.address ||
          result.prefill.tokenA.mint !== pool.tokenA.mint ||
          result.prefill.tokenB.mint !== pool.tokenB.mint
        )
          throw new Error("Recenter quote identity is unavailable");
        setPrepared({
          quote: result.quote,
          request: result.prefill.request,
          funding: snapshot,
          amount: formatNative(
            BigInt(result.prefill.request.amountNative),
            result.prefill.request.fundingMint === pool.tokenA.mint
              ? pool.tokenA.decimals
              : pool.tokenB.decimals,
          ),
          usd: "",
        });
        setInverted(result.prefill.inverted);
        setReviewShown(true);
        return;
      }
      for (let attempt = 0; attempt < 2; attempt++) {
        const snapshot = await readFunding(true, generation);
        if (!current()) return;
        const request = requestBody(snapshot);
        if (caps && BigInt(request.amountNative) > BigInt(caps.maxInputNative))
          throw new Error(
            `The new price requires more ${selectedFunding.symbol} than the previous quote allowed. Select Review position to prepare a new quote.`,
          );
        caps ??= { ...request.caps };
        request.caps = caps;
        if (snapshot.poolPrice.price !== pool.price || attempt > 0) {
          const previousReference =
            inverted && pool.price ? invertExactPrice(pool.price) : pool.price;
          const updatedReference = inverted
            ? invertExactPrice(snapshot.poolPrice.price)
            : snapshot.poolPrice.price;
          const updatedLower = inverted ? invertExactPrice(request.upperPrice) : request.lowerPrice;
          const updatedUpper = inverted ? invertExactPrice(request.lowerPrice) : request.upperPrice;
          setAdjustment(
            `Reference updated from ${formatPrice(previousReference)} to ${formatPrice(updatedReference)} ${frame.unit}. ${rangeMode === "manual" ? "Manual bounds retained" : "Percentage range recalculated"}: ${formatPrice(updatedLower)}–${formatPrice(updatedUpper)}.`,
          );
        }
        try {
          const result = await api<Quote>("/positions/quote", {
            method: "POST",
            body: request,
            cache: "no-store",
            signal: controller.signal,
          });
          if (!current()) return;
          const converted = convertedAt(snapshot);
          setPrepared({
            quote: result,
            request,
            funding: snapshot,
            amount: converted.token,
            usd: converted.usd,
          });
          setSettingsShown(false);
          setReviewShown(true);
          return;
        } catch (reason) {
          if (!current()) return;
          if (attempt === 0 && reason instanceof ApiError && reason.code === "PRICE_CHANGED")
            continue;
          throw reason;
        }
      }
    } catch (reason) {
      if (current())
        setQuoteError(
          reason instanceof ApiError && reason.code === "PRICE_CHANGED"
            ? "The pool price changed again while preparing your quote. Select Review position to try again."
            : reason instanceof Error
              ? reason.message
              : "Quote unavailable",
        );
    } finally {
      if (current()) setQuotePending(false);
    }
  }

  async function submitOpen(event: FormEvent) {
    event.preventDefault();
    const bound = submitted.current?.prepared ?? prepared;
    const expiresAt = bound ? Date.parse(bound.quote.expiresAt) : Number.NaN;
    if (
      viewer ||
      openInFlight.current ||
      operation ||
      !bound ||
      (!submitted.current && !recenter && stopBlocked) ||
      // A running autopilot owns new entries; it never blocks checking an already-submitted
      // request's uncertain outcome, only a fresh submission ([R1-F5]/[R1-F6]).
      (!submitted.current && autopilot.blocksManualOpening) ||
      (!submitted.current && openableReason(pool) !== null) ||
      (submitted.current !== null && submitted.current.identity !== identity) ||
      !runtime.executionReady ||
      !Number.isFinite(expiresAt) ||
      (expiresAt <= Date.now() && !(submitted.current && bound.quote.recenterReview))
    )
      return;
    submitted.current ??= { prepared: bound, identity };
    openInFlight.current = true;
    setOpenPending(true);
    setQuoteError(null);
    const submissionGeneration = quoteGeneration.current;
    const current = () =>
      submissionGeneration === quoteGeneration.current && activeIdentity.current === identity;
    try {
      const response = await api<Operation | { operation: Operation }>("/positions/open", {
        method: "POST",
        body: { quoteId: bound.quote.quoteId, idempotencyKey } satisfies OpenPositionRequest,
        sensitive: true,
      });
      if (!current()) return;
      setOperation("operation" in response ? response.operation : response);
      setReviewShown(false);
    } catch (reason) {
      if (!current()) return;
      if (
        bound.quote.recenterReview &&
        reason instanceof ApiError &&
        ["RECENTER_CHANGED", "QUOTE_EXPIRED"].includes(reason.code)
      ) {
        submitted.current = null;
        setIdempotencyKey(crypto.randomUUID());
        invalidate();
      }
      setQuoteError(reason instanceof Error ? reason.message : "Response unavailable");
    } finally {
      openInFlight.current = false;
      setOpenPending(false);
    }
  }

  return (
    <section className="wizard create-workspace" aria-labelledby="wizard-title">
      <header className="create-header">
        <button type="button" className="button ghost" aria-label="Back to pools" onClick={close}>
          ← Pools
        </button>
        <PoolStep
          pool={pool}
          frame={frame}
          first={first}
          second={second}
          reference={reference}
          unit={unit}
          inverted={inverted}
          onInvert={toggleOrientation}
          invertDisabled={
            recenter ||
            !!submitted.current ||
            !!operation ||
            (rangeMode === "manual" && !manual?.valid)
          }
          heading={heading}
          funding={funding}
          referenceAt={referenceAt}
          onPermissionResponse={onPermissionResponse}
        />
      </header>
      <div className="create-layout">
        <div id="position-market-chart" className="wizard-chart">
          <MarketChart
            pool={pool}
            referenceAt={prepared?.funding.observedAt ?? funding?.observedAt ?? referenceAt}
            referenceSource={funding ? "funding pool observation" : "catalog snapshot"}
            quoteMint={second.mint}
            current={reference}
            lower={recenter && !reviewShown ? undefined : chartLower}
            upper={recenter && !reviewShown ? undefined : chartUpper}
            lowerStop={recenter && !reviewShown ? undefined : chartLowerStop}
            upperStop={recenter && !reviewShown ? undefined : chartUpperStop}
          />
        </div>
        <section className="wizard-panel" aria-label="Position settings">
          <header className="order-panel-header">
            <h2 ref={panelHeading} tabIndex={-1}>
              New position
            </h2>
            <button
              type="button"
              ref={settingsButton}
              hidden={recenter}
              aria-haspopup="dialog"
              aria-expanded={settingsShown}
              disabled={recenter || !!submitted.current || !!operation}
              onClick={() => {
                setSettingsShown(true);
              }}
            >
              ⚙ Slippage <span className="num">{slippage}%</span>
            </button>
          </header>
          <div className="wizard-controls">
            {operation && (
              <>
                <AcceptedOperation initialOperation={operation} />
                <p className="hint accepted-leaving">
                  Leaving this page does not cancel the accepted position opening.
                </p>
              </>
            )}
            {prefill?.sourceOperationId && !operation && !viewer && (
              <section className="order-composition" aria-label="Set up again">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={recenter}
                    disabled={!!submitted.current}
                    onChange={(event) => {
                      setRecenter(event.target.checked);
                      invalidate();
                    }}
                  />
                  <span />
                  <b>Recenter original range and current auto-exit around latest price</b>
                </label>
                <p className="hint">
                  A new operation may pay new setup and network costs. Review a fresh quote, then
                  confirm to open.
                </p>
                {recenter && (
                  <>
                    <label htmlFor="recenter-slippage">New slippage % (optional, up to 5%)</label>
                    <input
                      id="recenter-slippage"
                      value={recenterSlippage}
                      inputMode="decimal"
                      disabled={!!submitted.current}
                      onChange={(event) => {
                        setRecenterSlippage(event.target.value);
                        invalidate();
                      }}
                    />
                    <p className="hint">
                      Keeps the original native funding amount, relative range and all other limits.
                      Leaving slippage blank keeps the active approved setting.
                    </p>
                    <p role="status">
                      {quotePending
                        ? "Preparing recentered position…"
                        : "Recentered range, auto-exit and composition appear after server review."}
                    </p>
                  </>
                )}
              </section>
            )}
            <fieldset
              className="order-inputs"
              hidden={recenter || !!submitted.current || !!operation}
              disabled={recenter || !!submitted.current || !!operation}
            >
              <RangeStep
                mode={rangeMode}
                setMode={(value) => {
                  if (value === "manual" && !manual) editManual(preview.min, preview.max);
                  setRangeMode(value);
                  invalidate();
                }}
                width={width}
                setWidth={(value) => {
                  setWidth(value);
                  invalidate();
                }}
                direction={direction}
                setDirection={(value) => {
                  setDirection(value);
                  invalidate();
                }}
                short={short}
                setShort={(value) => {
                  setShort(value);
                  invalidate();
                }}
                manualMin={manualMin}
                setManualMin={(value) => {
                  editManual(value, manualMax);
                }}
                manualMax={manualMax}
                setManualMax={(value) => {
                  editManual(manualMin, value);
                }}
                reference={reference}
                preview={preview}
                unit={unit}
                valid={rangeValid}
                outsidePrice={outsidePrice}
                scaledTokens={scaledTokens}
              />
              <EstimatedYieldRow
                period={yieldPeriod}
                onPeriodChange={(value) => {
                  setYieldPeriod(value);
                  writeYieldPeriod(value);
                }}
                estimate={yieldEstimate}
                pending={yieldPending}
                failed={yieldFailed}
              />
              <FundingStep
                settingsTarget={settingsTarget}
                limitsValid={limitsValid}
                pool={pool}
                fundingMint={fundingMint}
                setFundingMint={(value) => {
                  setFundingMint(value);
                  setInputAmount("");
                  setCurrency("token");
                  setDisplayCurrency("token");
                  invalidate();
                }}
                funding={funding}
                error={
                  fundingError ??
                  (inputAmount && evaluation.kind !== "refresh" ? balanceProblem : null)
                }
                pending={fundingPending}
                refreshFailed={fundingError !== null}
                configured={runtime.data?.wallet.configured === true}
                onRefresh={refreshFunding}
                usdAmount={currency === "usd" ? inputAmount : usdAmount}
                tokenAmount={currency === "token" ? inputAmount : amount}
                enteredAmount={inputAmount}
                enteredCurrency={currency}
                usdEnabled={priceUsd !== null && Number(priceUsd) > 0}
                setUsdAmount={(value) => {
                  setCurrency("usd");
                  setInputAmount(value);
                  invalidate();
                }}
                displayCurrency={displayCurrency}
                setDisplayCurrency={(value) => {
                  setDisplayCurrency(value);
                  invalidate();
                }}
                onCompletedEdit={completedEdit}
                setAmount={setAmount}
                autoSwap={autoSwap}
                setAutoSwap={(value) => {
                  setAutoSwap(value);
                  invalidate();
                }}
                slippage={slippage}
                setSlippage={(value) => {
                  setSlippage(value);
                  invalidate();
                }}
                priceImpact={priceImpact}
                setPriceImpact={(value) => {
                  setPriceImpact(value);
                  invalidate();
                }}
                priorityFee={priorityFee}
                setPriorityFee={(value) => {
                  setPriorityFee(value);
                  invalidate();
                }}
                networkFee={networkFee}
                setNetworkFee={(value) => {
                  setNetworkFee(value);
                  invalidate();
                }}
                rentCap={rentCap}
                setRentCap={(value) => {
                  setRentCap(value);
                  invalidate();
                }}
                solReserve={solReserve}
                setSolReserve={(value) => {
                  setSolReserve(value);
                  invalidate();
                }}
                maxAttempts={maxAttempts}
                scaledTokens={scaledTokens}
                setMaxAttempts={(value) => {
                  setMaxAttempts(value);
                  invalidate();
                }}
              >
                <section className="order-composition" aria-label="Funding preview">
                  {inputAmount && fundingValid ? (
                    <>
                      {positionPreview.displayResult ? (
                        <>
                          <p className="hint preview-status" role="status">
                            {positionPreview.result
                              ? `Preview fresh · calculated at ${new Date(positionPreview.result.calculationStartedAt).toLocaleTimeString()} · renews after 30 seconds`
                              : "Preview stale · requires recalculation before continuing."}
                            {!positionPreview.result &&
                              !positionPreview.error &&
                              " Updating funding preview…"}
                          </p>
                          {positionPreview.error && <p role="alert">{positionPreview.error}</p>}
                          <PositionPreview
                            composition={positionPreview.displayResult.composition}
                            pool={pool}
                            caps={
                              (JSON.parse(positionPreview.displayResult.requestKey) as QuoteRequest)
                                .caps
                            }
                            frame={frame}
                          />
                          <details className="composition-minimum-details">
                            <summary>Lower composition estimate</summary>
                            <CompositionMinimums
                              composition={positionPreview.displayResult.composition}
                              pool={pool}
                            />
                          </details>
                        </>
                      ) : (
                        <div className="composition-placeholder" aria-busy={!positionPreview.error}>
                          <p role={positionPreview.error ? "alert" : "status"}>
                            {positionPreview.error ?? "Updating funding preview…"}
                          </p>
                          <div className="composition-skeleton" aria-hidden="true">
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                          </div>
                        </div>
                      )}
                      {positionPreview.error && (
                        <button type="button" onClick={positionPreview.retry}>
                          Retry preview
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="hint">Enter an amount to estimate the position composition.</p>
                  )}
                </section>
              </FundingStep>
              <ExitStep
                pool={pool}
                below={stopBelow}
                above={stopAbove}
                onChangeStops={(next) => {
                  setStopBelow(next.below);
                  setStopAbove(next.above);
                  invalidate();
                }}
                mirror={stopMirror}
                onMirror={setStopMirror}
                onValidityChange={(invalid) => {
                  setStopInvalid(invalid);
                  // A quote requested before the draft went bad must not reach review.
                  if (invalid) invalidate();
                }}
                geometryError={previewStops.error}
                rows={stopRows}
                usdReason={stopUsdReason}
                notice={
                  stopBasisInverted === inverted
                    ? stopPrefill.notice
                    : [
                        stopPrefill.notice,
                        `Distances are measured in ${priceFrame(pool.tokenA, pool.tokenB, stopBasisInverted).unit}. Direction and preview use ${unit}.`,
                      ]
                        .filter(Boolean)
                        .join(" ")
                }
                targetMint={targetMint}
                setTargetMint={(value) => {
                  setTargetMint(value);
                  invalidate();
                }}
                preview={preview}
                stops={previewStops}
                reference={reference}
                unit={unit}
                scaledTokens={scaledTokens}
              />
            </fieldset>
            {/* These messages are trailing children of `.wizard-controls`, which carries no padding
                of its own: every band supplies its own inset. Without this wrapper they sit flush
                against the panel border while their neighbours are indented. */}
            <div className="order-status">
              {quotePending && (
                <p className="hint" role="status">
                  Updating funding and preparing an exact quote…
                </p>
              )}
              {quotePending && (
                <button type="button" className="button ghost" onClick={cancel}>
                  Cancel preparation
                </button>
              )}
              {adjustment && (
                <p className="hint" role="status">
                  {adjustment}
                </p>
              )}
              {quoteError && !reviewShown && !submitted.current && (
                <p className="inline-alert error" role="alert">
                  {quoteError}
                </p>
              )}
              {submitted.current && !operation && !reviewShown && (
                <div className="inline-alert warning" role="status">
                  {openPending
                    ? "Submitting opening… Checking whether the opening was accepted."
                    : "Submission outcome is uncertain. Check whether this opening was accepted before starting another."}
                  <button
                    type="button"
                    onClick={() => {
                      setReviewShown(true);
                    }}
                  >
                    View submitted quote
                  </button>
                </div>
              )}
              {operation?.state === "blocked" && operation.retry?.cancel.allowed && (
                <p className="inline-alert warning" role="status">
                  If this opening cannot continue, use Cancel opening from My positions to release
                  eligible funds without an exit swap.
                </p>
              )}
            </div>
          </div>
          {!operation && !submitted.current && (
            <footer className="wizard-footer">
              <div className="order-value">
                <span>Estimated position value</span>
                <strong className="num">
                  {formatFundingUsd(
                    recenter
                      ? null
                      : (positionPreview.displayResult?.composition.totalDepositUsd ?? null),
                  )}
                </strong>
              </div>
              {!recenter && (
                <small>
                  Swap value change{" "}
                  {formatFundingUsd(
                    positionPreview.displayResult?.composition.swap?.valueDifferenceUsd ?? null,
                  )}{" "}
                  · network ≤ {networkFee} SOL · rent ≤ {rentCap} SOL
                </small>
              )}
              {!recenter && funding && (
                <details className="footer-funding">
                  <summary>Funding and observation</summary>
                  <FundingSummary
                    amount={prepared?.amount ?? amount}
                    usd={prepared?.usd ?? usdAmount}
                    symbol={selectedFunding.symbol}
                    observedAt={(prepared?.funding ?? funding).observedAt}
                    pending={fundingPending}
                    failed={fundingError !== null}
                    onRefresh={refreshFunding}
                  />
                </details>
              )}
              <button
                type="button"
                ref={reviewButton}
                className="button primary"
                disabled={
                  leaving ||
                  quotePending ||
                  (recenter
                    ? !recenterValid
                    : !rangeValid ||
                      !fundingValid ||
                      !targetMint ||
                      stopBlocked ||
                      !positionPreview.result ||
                      fundingStale(funding, now))
                }
                aria-describedby="review-disabled-reason"
                onClick={() => void getQuote()}
              >
                {quotePending
                  ? "Checking on-chain…"
                  : recenter
                    ? "Review recentered position"
                    : "Review position"}
              </button>
              <small id="review-disabled-reason">
                {recenter
                  ? recenterValid
                    ? "The server calculates the new range before confirmation."
                    : "Enter slippage from 0% to 5%."
                  : !rangeValid
                    ? "Complete a valid range."
                    : !inputAmount
                      ? "Enter a funding amount."
                      : !fundingValid
                        ? "Check the amount and execution limits above."
                        : !targetMint
                          ? "Select a return token."
                          : stopBlocked
                            ? "Fix the stop distance above."
                            : !positionPreview.result
                              ? "A fresh matching preview is required."
                              : fundingStale(funding, now)
                                ? "Refresh the funding observation."
                                : "Exact amounts and limits appear before confirmation."}
              </small>
            </footer>
          )}
        </section>
      </div>
      {settingsShown && (
        <WizardModal
          title="Execution settings"
          onDismiss={() => {
            setSettingsShown(false);
          }}
          restoreTarget={settingsButton}
          fallbackTarget={panelHeading}
          className="execution-settings"
        >
          <p className="hint">Swaps route through {dexName(pool.dex)} pools only.</p>
          <div ref={setSettingsTarget} />
          <button
            type="button"
            className="button primary"
            onClick={() => {
              setSettingsShown(false);
            }}
          >
            Done
          </button>
        </WizardModal>
      )}
      {reviewShown && prepared && !operation && (
        <WizardModal
          title="Review position"
          headingContent={
            <>
              <TokenPair frame={frame} />
              <span>
                Open {frame.pair}
                <small>Exact quote from the server · check and confirm</small>
              </span>
              {permissionResponse?.poolAddress === pool.address && (
                <TokenPermissions
                  poolAddress={pool.address}
                  knownResponse={permissionResponse.value}
                />
              )}
            </>
          }
          headerTarget={setReviewHeaderTarget}
          onDismiss={() => {
            setReviewShown(false);
            if (!submitted.current) cancel();
          }}
          restoreTarget={reviewButton}
          fallbackTarget={panelHeading}
        >
          <div className="review-controls">
            <ReviewStep
              pool={pool}
              quote={prepared.quote}
              funding={prepared.funding}
              request={prepared.request}
              amount={prepared.amount}
              usd={prepared.usd}
              headerTarget={reviewHeaderTarget}
              unit={unit}
              inverted={inverted}
              reference={
                inverted
                  ? invertExactPrice(prepared.request.referencePrice)
                  : prepared.request.referencePrice
              }
              pending={openPending}
              submitted={!!submitted.current}
              identityMatches={!submitted.current || submitted.current.identity === identity}
              error={quoteError}
              executorState={
                runtime.freshness === "fresh"
                  ? (runtime.data?.executor.state ?? "unknown")
                  : runtime.freshness
              }
              executorReady={
                runtime.executionReady &&
                (!submitted.current || submitted.current.identity === identity)
              }
              autopilotBlocking={autopilot.blocksManualOpening}
              onSubmit={submitOpen}
              actionTarget={reviewActionTarget}
              scaledTokens={scaledTokens}
            />
          </div>
          <footer className="review-footer" ref={setReviewActionTarget}>
            <button
              type="button"
              className="button ghost"
              onClick={() => {
                setReviewShown(false);
                if (!submitted.current) cancel();
              }}
            >
              {submitted.current ? "Close review" : "Back"}
            </button>
            {submitted.current && onPositions && (
              <button type="button" className="button" onClick={onPositions}>
                Go to My positions
              </button>
            )}
            {!submitted.current && (
              <button
                type="button"
                className="quote-refresh"
                aria-label="Get new quote"
                disabled={quotePending}
                onClick={() => void getQuote(true)}
              >
                Price moved? Get a new quote
              </button>
            )}
          </footer>
        </WizardModal>
      )}
    </section>
  );
}

function WizardModal({
  title,
  headingContent,
  headerTarget,
  children,
  onDismiss,
  restoreTarget,
  fallbackTarget,
  className = "",
}: {
  title: string;
  headingContent?: ReactNode;
  headerTarget?: (node: HTMLSpanElement | null) => void;
  children: ReactNode;
  onDismiss: () => void;
  restoreTarget: RefObject<HTMLButtonElement | null>;
  fallbackTarget: RefObject<HTMLHeadingElement | null>;
  className?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const node = dialog.current;
    const target = restoreTarget.current;
    const fallback = fallbackTarget.current;
    node?.showModal();
    titleRef.current?.focus();
    return () => {
      node?.close();
      (target && !target.disabled ? target : fallback)?.focus({
        preventScroll: true,
      });
    };
  }, [restoreTarget, fallbackTarget]);
  return createPortal(
    <dialog
      ref={dialog}
      className={"wizard-modal " + className}
      aria-label={title}
      onCancel={(event) => {
        event.preventDefault();
        onDismiss();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onDismiss();
        }
        if (event.key !== "Tab") return;
        const focusable = Array.from(
          dialog.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled), input:not(:disabled), select:not(:disabled), summary, a[href], [tabindex="0"]',
          ) ?? [],
        ).filter((node) => {
          for (
            let ancestor = node.parentElement;
            ancestor && ancestor !== dialog.current;
            ancestor = ancestor.parentElement
          ) {
            if (
              ancestor.tagName === "DETAILS" &&
              !ancestor.hasAttribute("open") &&
              !(node.tagName === "SUMMARY" && node.parentElement === ancestor)
            )
              return false;
          }
          return true;
        });
        const first = focusable.at(0),
          last = focusable.at(-1);
        if (!first) {
          event.preventDefault();
          titleRef.current?.focus();
        } else if (
          event.shiftKey &&
          (document.activeElement === first || document.activeElement === titleRef.current)
        ) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }}
    >
      <header className="modal-heading">
        <h2 ref={titleRef} tabIndex={-1}>
          {headingContent ?? title}
        </h2>
        {headerTarget && <span className="review-validity" ref={headerTarget} />}
        <button type="button" aria-label={"Close " + title} onClick={onDismiss}>
          ×
        </button>
      </header>
      {children}
    </dialog>,
    document.body,
  );
}

function FundingSummary({
  amount,
  usd,
  symbol,
  observedAt,
  pending,
  failed = false,
  onRefresh,
}: {
  amount: string;
  usd: string;
  symbol: string;
  observedAt: string;
  pending: boolean;
  failed?: boolean;
  onRefresh?: () => void;
}) {
  const refresh = useRefreshPhase({ pending, failed });
  return (
    <div className="funding-summary hint">
      Funding: {formatFundingToken(amount)} {symbol} · ≈ {formatFundingUsd(usd)}
      <p>Funding checked: {new Date(observedAt).toLocaleTimeString()}</p>
      <details>
        <summary>Exact funding values</summary>
        <ReadonlyValue label={`funding amount, ${symbol}`} value={amount} />
        <ReadonlyValue label="funding value, USD" value={usd || "Unavailable"} />
      </details>
      {onRefresh && (
        <RefreshButton
          phase={refresh.phase}
          ariaLabel="Refresh funding"
          label="Refresh funding"
          onClick={() => {
            if (refresh.start()) onRefresh();
          }}
        />
      )}
    </div>
  );
}

function PoolStep({
  pool,
  frame,
  first,
  second,
  reference,
  unit,
  inverted,
  onInvert,
  invertDisabled,
  heading,
  funding,
  referenceAt,
  onPermissionResponse,
}: {
  pool: Pool;
  frame: PriceFrame;
  first: Token;
  second: Token;
  reference: string;
  unit: string;
  inverted: boolean;
  onInvert: () => void;
  invertDisabled: boolean;
  heading: RefObject<HTMLHeadingElement | null>;
  funding: FundingSnapshot | null;
  referenceAt?: string;
  onPermissionResponse: (response: PoolTokenPermissionsResponse | null) => void;
}) {
  return (
    <div className="pool-toolbar">
      <div className="pool-hero">
        <TokenPair frame={frame} />
        <div>
          <h1 id="wizard-title" ref={heading} tabIndex={-1}>
            <TokenPairName frame={frame} />
          </h1>
          <TokenPermissions poolAddress={pool.address} onResponse={onPermissionResponse} />
          <div className="pool-toolbar-meta">
            <span className="tag tier num">{formatPercent(pool.feePercent)}</span>
            <DexBadge dex={pool.dex} />
            <span className="num" title={pool.address}>
              {shorten(pool.address, 6)}
            </span>
            <a href={dexPoolUrl(pool.dex, pool.address)} target="_blank" rel="noreferrer">
              {dexName(pool.dex)} ↗
            </a>
            <details>
              <summary>Pool details</summary>
              <dl>
                <div>
                  <dt>Pool address</dt>
                  <dd>{pool.address}</dd>
                </div>
                <div>
                  <dt>Mint {first.symbol}</dt>
                  <dd>{first.mint}</dd>
                </div>
                <div>
                  <dt>Mint {second.symbol}</dt>
                  <dd>{second.mint}</dd>
                </div>
              </dl>
              <p>{dexName(pool.dex)} · Solana Mainnet</p>
              <p>
                Reference observed{" "}
                {funding
                  ? new Date(funding.observedAt).toLocaleString()
                  : referenceAt
                    ? new Date(referenceAt).toLocaleString()
                    : "time unavailable"}{" "}
                · {funding ? "funding pool observation, confirmed" : "catalog snapshot"}
              </p>
              <p>Inversion changes the display; exact manual bounds remain unchanged.</p>
            </details>
          </div>
        </div>
      </div>
      <div className="pool-reference">
        <strong className="num">
          {formatPrice(reference)} <small>{unit}</small>
        </strong>
        <span>
          {first.symbol} USD{" "}
          {formatFundingUsd(
            funding?.assets.find((asset) => asset.mint === first.mint)?.priceUsd ?? null,
          )}
        </span>
      </div>
      <dl className="pool-catalog-stats">
        <div>
          <dt>TVL</dt>
          <dd className="num">{formatFundingUsd(pool.tvlUsd)}</dd>
        </div>
        <div>
          <dt>Catalog volume</dt>
          <dd className="num">{formatFundingUsd(pool.volumeUsd)}</dd>
        </div>
        <div>
          <dt>Catalog fees</dt>
          <dd className="num">{formatFundingUsd(pool.feesUsd)}</dd>
        </div>
        <small>Statistics period unavailable</small>
      </dl>
      <button
        type="button"
        className="button ghost invert-price"
        onClick={onInvert}
        disabled={invertDisabled}
        aria-label={"Show price as " + priceFrame(pool.tokenA, pool.tokenB, !inverted).unit}
      >
        ⇄
      </button>
      {pool.warning && <p className="inline-alert warning pool-warning">{pool.warning}</p>}
    </div>
  );
}

interface RangeStepProps {
  mode: RangeEntryMode;
  setMode: (v: RangeEntryMode) => void;
  width: number;
  setWidth: (v: number) => void;
  direction: RangeDirection;
  setDirection: (v: RangeDirection) => void;
  short: number;
  setShort: (v: number) => void;
  manualMin: string;
  setManualMin: (v: string) => void;
  manualMax: string;
  setManualMax: (v: string) => void;
  reference: string;
  preview: { min: string; max: string };
  unit: string;
  valid: boolean;
  /** A Raydium range that is otherwise valid but leaves out the current price. */
  outsidePrice: boolean;
  scaledTokens: Token[];
}
function RangeStep(props: RangeStepProps) {
  return (
    <section className="order-section range-section" aria-label="Range">
      <header className="order-section-heading">
        <h3>Range</h3>
        <div className="segmented">
          <button
            type="button"
            aria-pressed={props.mode === "percent"}
            className={props.mode === "percent" ? "active" : ""}
            onClick={() => {
              props.setMode("percent");
            }}
          >
            Presets
          </button>
          <button
            type="button"
            aria-pressed={props.mode === "manual"}
            className={props.mode === "manual" ? "active" : ""}
            onClick={() => {
              props.setMode("manual");
            }}
          >
            Min / max
          </button>
        </div>
      </header>
      {props.mode === "percent" ? (
        <>
          <fieldset>
            <legend>Width</legend>
            <div className="choice-grid range-width-options">
              {RANGE_WIDTHS.map((value) => (
                <button
                  type="button"
                  key={value}
                  aria-pressed={props.width === value}
                  className={props.width === value ? "selected" : ""}
                  onClick={() => {
                    props.setWidth(value);
                  }}
                >
                  {value}%
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Placement</legend>
            <div className="choice-grid thirds">
              <button
                type="button"
                aria-pressed={props.direction === "down"}
                className={props.direction === "down" ? "selected" : ""}
                onClick={() => {
                  props.setDirection("down");
                }}
              >
                ↓ Below
              </button>
              <button
                type="button"
                aria-pressed={props.direction === "balanced"}
                className={props.direction === "balanced" ? "selected" : ""}
                onClick={() => {
                  props.setDirection("balanced");
                }}
              >
                Centered
              </button>
              <button
                type="button"
                aria-pressed={props.direction === "up"}
                className={props.direction === "up" ? "selected" : ""}
                onClick={() => {
                  props.setDirection("up");
                }}
              >
                ↑ Above
              </button>
            </div>
          </fieldset>
          {props.direction !== "balanced" && (
            <fieldset className="offset-panel">
              <legend>{props.direction === "down" ? "Above current" : "Below current"}</legend>
              <div className="choice-grid">
                {OFFSETS.map((value) => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={props.short === value}
                    className={props.short === value ? "selected" : ""}
                    disabled={value > props.width / 2}
                    onClick={() => {
                      props.setShort(value);
                    }}
                  >
                    <span>{value}%</span>
                    {value === props.width / 2 && <span>symmetric</span>}
                  </button>
                ))}
              </div>
              {props.width === 5 && (
                <p className="inline-alert warning">
                  A 5% width has no valid short-offset preset. Choose Centered or manual bounds.
                </p>
              )}
            </fieldset>
          )}
        </>
      ) : (
        <div className="form-grid">
          <label>
            Min
            <input
              inputMode="decimal"
              aria-invalid={!props.valid}
              aria-describedby="range-error"
              value={props.manualMin}
              onChange={(event) => {
                props.setManualMin(event.target.value);
              }}
              placeholder="90"
            />
          </label>
          <label>
            Max
            <input
              inputMode="decimal"
              aria-invalid={!props.valid}
              aria-describedby="range-error"
              value={props.manualMax}
              onChange={(event) => {
                props.setManualMax(event.target.value);
              }}
              placeholder="110"
            />
          </label>
        </div>
      )}
      <PriceScale
        min={props.preview.min}
        max={props.preview.max}
        current={props.reference}
        preliminary
        unit={props.unit}
      />
      <ScaledPoolUnitsNote tokens={props.scaledTokens} />
      {!props.valid && (
        <p id="range-error" className="form-error" role="alert">
          {props.outsidePrice
            ? `Raydium opens need the current price inside the range: min below ${formatPrice(props.reference)} ${props.unit} and max above it.`
            : "Min must be below max; a directional offset cannot exceed half the width."}
        </p>
      )}
      <details className="range-explanation">
        <summary>About this range</summary>
        <p className="hint">
          This is a preliminary range around the captured price. The server rounds it to valid pool
          ticks; exact bounds appear before confirmation. Prices are in {props.unit}, increasing
          from min to max.
        </p>
      </details>
    </section>
  );
}

interface FundingProps {
  children: ReactNode;
  settingsTarget: HTMLElement | null;
  limitsValid: boolean;
  funding: FundingSnapshot | null;
  error: string | null;
  pending: boolean;
  /** The last funding read failed; the balance problem in `error` is not a refresh failure. */
  refreshFailed: boolean;
  configured: boolean;
  onRefresh: () => void;
  usdAmount: string;
  tokenAmount: string;
  enteredAmount: string;
  enteredCurrency: "token" | "usd";
  usdEnabled: boolean;
  setUsdAmount: (value: string) => void;

  pool: Pool;
  fundingMint: string;
  setFundingMint: (v: string) => void;
  displayCurrency: "token" | "usd";
  setDisplayCurrency: (v: "token" | "usd") => void;
  onCompletedEdit: () => void;
  setAmount: (v: string) => void;
  autoSwap: boolean;
  setAutoSwap: (v: boolean) => void;
  slippage: string;
  setSlippage: (v: string) => void;
  priceImpact: string;
  setPriceImpact: (v: string) => void;
  priorityFee: string;
  setPriorityFee: (v: string) => void;
  networkFee: string;
  setNetworkFee: (v: string) => void;
  rentCap: string;
  setRentCap: (v: string) => void;
  solReserve: string;
  setSolReserve: (v: string) => void;
  maxAttempts: number;
  setMaxAttempts: (v: number) => void;
  scaledTokens: Token[];
}
function FundingStep(p: FundingProps) {
  const selected = p.pool.tokenA.mint === p.fundingMint ? p.pool.tokenA : p.pool.tokenB;
  const balancesRefresh = useRefreshPhase({ pending: p.pending, failed: p.refreshFailed });
  let emptyOrZero = p.enteredAmount === "";
  try {
    emptyOrZero ||= /^\d+(\.\d+)?$/.test(p.enteredAmount) && decimal(p.enteredAmount).units === 0n;
  } catch {
    emptyOrZero = false;
  }
  const available = p.funding?.assets.find((asset) => asset.mint === selected.mint);
  const usdCounterpart = p.usdAmount || (emptyOrZero ? "0" : "");
  const tokenCounterpart = p.tokenAmount || (emptyOrZero ? "0" : "");
  return (
    <section className="order-section amount-section" aria-label="Amount">
      <header className="order-section-heading">
        <h3>Amount</h3>
        <fieldset>
          <legend>Pay with</legend>
          <div className="choice-grid halves">
            {[p.pool.tokenA, p.pool.tokenB].map((token) => (
              <button
                type="button"
                key={token.mint}
                aria-pressed={p.fundingMint === token.mint}
                className={p.fundingMint === token.mint ? "selected" : ""}
                onClick={() => {
                  p.setFundingMint(token.mint);
                }}
              >
                {token.symbol}
              </button>
            ))}
          </div>
        </fieldset>
      </header>
      <div className="funding-card">
        <div className="funding-card-heading">
          <TokenIcon token={selected} />
          <b>{selected.symbol}</b>
        </div>
        <label>
          <span className="sr-only">
            {p.displayCurrency === "token" ? `Amount, ${selected.symbol}` : "≈ USD"}
          </span>
          <input
            inputMode="decimal"
            aria-invalid={!!p.error}
            aria-describedby={p.error ? "funding-error" : undefined}
            value={p.displayCurrency === "token" ? p.tokenAmount : p.usdAmount}
            onChange={(event) => {
              (p.displayCurrency === "token" ? p.setAmount : p.setUsdAmount)(event.target.value);
            }}
            onBlur={p.onCompletedEdit}
            onKeyDown={(event) => {
              if (event.key === "Enter") p.onCompletedEdit();
            }}
            placeholder="0"
          />
        </label>
        <label>
          <span className="sr-only">Denomination</span>
          <select
            value={p.displayCurrency}
            onChange={(event) => {
              p.setDisplayCurrency(event.target.value as "token" | "usd");
            }}
          >
            <option value="token">{selected.symbol}</option>
            <option value="usd" disabled={!p.usdEnabled}>
              USD
            </option>
          </select>
        </label>
      </div>
      <ScaledPoolUnitsNote tokens={p.scaledTokens} />
      <section className="funding-balances" aria-label="Available for this position">
        <header className="funding-balances-header">
          <span>
            Available{" "}
            <b className="num">
              {available?.availableNative == null
                ? "Unavailable"
                : formatFundingToken(
                    formatNative(BigInt(available.availableNative), available.decimals),
                  )}{" "}
              {selected.symbol}
            </b>
          </span>
          {p.configured && (
            <RefreshButton
              phase={balancesRefresh.phase}
              ariaLabel="Refresh balances"
              title="Refresh balances"
              onClick={() => {
                if (balancesRefresh.start()) p.onRefresh();
              }}
            />
          )}
        </header>
        <small>
          Uses {p.tokenAmount ? formatFundingToken(p.tokenAmount) : "—"} {selected.symbol} ·
          available value {formatFundingUsd(available?.availableUsd ?? null)}
        </small>
        <details className="balance-disclosure">
          <summary>Available balances and details</summary>
          <small>
            ≈{" "}
            {p.displayCurrency === "token"
              ? formatFundingUsd(usdCounterpart)
              : `${formatFundingToken(tokenCounterpart)} ${selected.symbol}`}
          </small>

          <ReadonlyValue label="last edited funding amount" value={p.enteredAmount} />
          <ReadonlyValue
            label="last edited denomination"
            value={p.enteredCurrency === "usd" ? "USD" : selected.symbol}
          />
          <p className="hint">
            Includes native SOL and each token’s standard wallet account. SOL must also cover fees,
            account rent and amounts reserved for pending position changes.
          </p>
          {!p.configured && <p>Import a wallet in Settings first.</p>}
          {p.pending && <p role="status">Loading balances…</p>}
          {p.funding?.assets.map((asset) => (
            <p className="amount-line scroll-value" key={asset.mint}>
              {asset.symbol}:{" "}
              {asset.availableNative === null
                ? "Unavailable"
                : formatFundingToken(
                    formatNative(BigInt(asset.availableNative), asset.decimals),
                  )}{" "}
              · ≈ {formatFundingUsd(asset.availableUsd)}
            </p>
          ))}
          {p.funding && (
            <>
              <small>Funding checked {new Date(p.funding.observedAt).toLocaleTimeString()}</small>
              <details>
                <summary>Exact balances and observation details</summary>
                {p.funding.assets.map((asset) => (
                  <div className="exact-balance" key={asset.mint}>
                    <ReadonlyValue
                      label={`${asset.symbol} available`}
                      value={
                        asset.availableNative === null
                          ? "Unavailable"
                          : `${formatNative(BigInt(asset.availableNative), asset.decimals)} ${asset.symbol}`
                      }
                    />
                    <ReadonlyValue
                      label={`${asset.symbol} indicative USD`}
                      value={
                        asset.availableUsd === null ? "Unavailable" : `${asset.availableUsd} USD`
                      }
                    />
                  </div>
                ))}
                <p>{p.funding.priceSource}. USD is indicative and may reuse a 30-second anchor.</p>
                <ReadonlyValue label="pool price" value={p.funding.poolPrice.price} />
                <ReadonlyValue
                  label="last finalized inventory slot"
                  value={p.funding.inventorySlot ?? "Unavailable"}
                />
                <ReadonlyValue
                  label="pending reserve floor"
                  value={
                    p.funding.reserveFloorLamports === null
                      ? "Unavailable"
                      : `${formatNative(BigInt(p.funding.reserveFloorLamports), 9)} SOL`
                  }
                />
                <p>
                  Pool price commitment: {p.funding.poolPrice.commitment}. SOL may be from an
                  earlier read; these observations are not atomic.
                </p>
              </details>
            </>
          )}
          {p.funding?.errors.map((error) => (
            <p className="form-error" key={error}>
              {error}
            </p>
          ))}
        </details>
      </section>
      {p.error && (
        <p id="funding-error" className="form-error" role="alert">
          {p.error}
        </p>
      )}
      <div className="funding-composition-group">
        <label className="toggle">
          <input
            type="checkbox"
            checked={p.autoSwap}
            onChange={(event) => {
              p.setAutoSwap(event.target.checked);
            }}
          />
          <span />
          <b>Automatically swap the missing token</b>
          <small>
            {p.autoSwap ? "Enabled" : "Disabled"}. Only bounded {dexName(p.pool.dex)} routes are
            allowed.
          </small>
        </label>
        {!p.autoSwap && (
          <p className="hint">
            Only the selected funding token is used. A range requiring the other token may need
            auto-swap.
          </p>
        )}
        {p.children}
      </div>
      {p.settingsTarget &&
        createPortal(
          <>
            <fieldset>
              <legend>Maximum slippage</legend>
              <div className="segmented">
                {["0.3", "0.5", "1.0"].map((value) => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={p.slippage === value}
                    className={p.slippage === value ? "active" : ""}
                    onClick={() => {
                      p.setSlippage(value);
                    }}
                  >
                    {value}%
                  </button>
                ))}
              </div>
            </fieldset>
            <div
              className="form-grid"
              role="group"
              aria-label="Execution limits"
              aria-describedby={!p.limitsValid ? "limits-error" : undefined}
            >
              <label>
                Maximum slippage, %
                <input
                  inputMode="decimal"
                  value={p.slippage}
                  onChange={(event) => {
                    p.setSlippage(event.target.value);
                  }}
                />
              </label>
              <label>
                Maximum price impact, %
                <input
                  inputMode="decimal"
                  value={p.priceImpact}
                  onChange={(event) => {
                    p.setPriceImpact(event.target.value);
                  }}
                />
              </label>
              <label>
                Maximum total network fee, SOL
                <input
                  inputMode="decimal"
                  value={p.networkFee}
                  onChange={(event) => {
                    p.setNetworkFee(event.target.value);
                  }}
                />
              </label>
              <label>
                Maximum priority fee, SOL
                <input
                  inputMode="decimal"
                  value={p.priorityFee}
                  onChange={(event) => {
                    p.setPriorityFee(event.target.value);
                  }}
                />
              </label>
              <label>
                Maximum rent, SOL
                <input
                  inputMode="decimal"
                  value={p.rentCap}
                  onChange={(event) => {
                    p.setRentCap(event.target.value);
                  }}
                />
              </label>
              <label>
                Minimum SOL reserve
                <input
                  inputMode="decimal"
                  value={p.solReserve}
                  onChange={(event) => {
                    p.setSolReserve(event.target.value);
                  }}
                />
              </label>
              <label>
                Maximum attempts
                <input
                  type="number"
                  min={1}
                  max={20}
                  step={1}
                  value={p.maxAttempts}
                  onChange={(event) => {
                    p.setMaxAttempts(event.target.valueAsNumber);
                  }}
                />
              </label>
            </div>
            {!p.limitsValid && (
              <p id="limits-error" className="form-error" role="alert">
                Enter valid execution limits: slippage and impact from 0 to less than 100%, a
                positive network fee, nonnegative SOL caps and reserve, and 1–20 whole attempts.
              </p>
            )}
          </>,
          p.settingsTarget,
        )}
    </section>
  );
}

interface ExitProps {
  pool: Pool;
  below: string;
  above: string;
  onChangeStops: (next: { below: string; above: string }) => void;
  mirror: boolean;
  onMirror: (value: boolean) => void;
  onValidityChange: (invalid: boolean) => void;
  geometryError?: string;
  rows: { below: StopDistanceRowView; above: StopDistanceRowView };
  usdReason?: string;
  notice: string | null;
  targetMint: string;
  setTargetMint: (v: string) => void;
  preview: { min: string; max: string };
  stops: { lower?: string; upper?: string };
  reference: string;
  unit: string;
  scaledTokens: Token[];
}
function ExitStep(p: ExitProps) {
  return (
    <section className="order-section exit-section" aria-label="Auto-exit">
      <header className="order-section-heading">
        <h3>Auto-exit</h3>
      </header>
      <StopDistanceControl
        below={p.below}
        above={p.above}
        onChange={p.onChangeStops}
        mirror={p.mirror}
        onMirror={p.onMirror}
        onValidityChange={p.onValidityChange}
        geometryError={p.geometryError}
        rows={p.rows}
        unit={p.unit}
        usdReason={p.usdReason}
        notice={p.notice}
        tokens={[p.pool.tokenA, p.pool.tokenB]}
        targetMint={p.targetMint}
        onTarget={p.setTargetMint}
        preferredMint={preferredTarget(p.pool) || undefined}
      />
      {!p.targetMint && (
        <p className="inline-alert warning">
          This pair has no canonical SOL, USDT, or USDC mint. Select one of the two mints
          explicitly; the system will not choose a risky asset automatically.
        </p>
      )}
      <PriceScale
        min={p.preview.min}
        max={p.preview.max}
        current={p.reference}
        lowerStop={p.stops.lower}
        upperStop={p.stops.upper}
        preliminary
        unit={p.unit}
      />
      <ScaledPoolUnitsNote tokens={p.scaledTokens} />
      <details>
        <summary>Exit policy details</summary>
        <p className="hint">
          Stops are calculated from the corresponding actual boundary in the saved percentage basis.
          Preview prices are in {p.unit}. A stop does not guarantee an execution price. Manual close
          remains available.
        </p>
      </details>
    </section>
  );
}

interface ReviewProps {
  funding: FundingSnapshot;
  request: QuoteRequest;
  amount: string;
  usd: string;
  headerTarget: HTMLElement | null;
  pool: Pool;
  quote: Quote;
  unit: string;
  inverted: boolean;
  reference: string;
  pending: boolean;
  submitted: boolean;
  identityMatches: boolean;
  error: string | null;
  executorState: string;
  executorReady: boolean;
  /** A running autopilot owns new entries; submitting is refused until it pauses or stops. */
  autopilotBlocking: boolean;
  onSubmit: (event: FormEvent) => Promise<void>;
  actionTarget: HTMLElement | null;
  scaledTokens: Token[];
}
function ReviewStep(p: ReviewProps) {
  const viewer = useViewer();
  const frame = priceFrame(p.pool.tokenA, p.pool.tokenB, p.inverted);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const updateNow = () => {
      setNow(Date.now());
    };
    const timer = window.setInterval(() => {
      updateNow();
    }, 1_000);
    const expiresAt = Date.parse(p.quote.expiresAt);
    const expiryDelay = expiresAt - Date.now();
    const expiryTimer =
      Number.isFinite(expiryDelay) && expiryDelay > 0
        ? window.setTimeout(updateNow, Math.min(expiryDelay, 2_147_483_647))
        : 0;
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(expiryTimer);
    };
  }, [p.quote.expiresAt]);
  const lower = p.inverted ? invertExactPrice(p.quote.upperPrice) : p.quote.lowerPrice;
  const upper = p.inverted ? invertExactPrice(p.quote.lowerPrice) : p.quote.upperPrice;
  const lowerStop = p.inverted
    ? p.quote.exitPolicy.upperTrigger && invertExactPrice(p.quote.exitPolicy.upperTrigger)
    : p.quote.exitPolicy.lowerTrigger;
  const upperStop = p.inverted
    ? p.quote.exitPolicy.lowerTrigger && invertExactPrice(p.quote.exitPolicy.lowerTrigger)
    : p.quote.exitPolicy.upperTrigger;
  // The quote's policy is canonical; map it to the display frame before naming a side.
  let displayStopPair: { below: string | null; above: string | null } = {
    below: null,
    above: null,
  };
  try {
    const distances = stopDistances(p.quote.exitPolicy);
    displayStopPair = {
      below: p.inverted ? distances.upper : distances.lower,
      above: p.inverted ? distances.lower : distances.upper,
    };
  } catch {
    /* A malformed stored policy has no distances to show; the summary reads "—". */
  }
  const expiresAt = Date.parse(p.quote.expiresAt);
  const expired = !Number.isFinite(expiresAt) || expiresAt <= now;
  const recenterReplay = p.submitted && !!p.quote.recenterReview;
  const nativeAmount = (value: string, mint: string | undefined) => {
    const token = poolToken(p.pool, mint);
    return token
      ? `${formatNative(BigInt(value), token.decimals)} ${token.symbol}`
      : `${value} native units`;
  };
  const usdValue = (value: string | null | undefined) =>
    value ? formatFundingUsd(value) : "— · USD not provided";
  const remainingSeconds = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  const compositionAmount = (value: OpenComposition["depositA"]) =>
    nativeAmount(value.native, value.mint);
  const routed = routedQuoteLines(p.quote, poolToken(p.pool, p.quote.routes[0]?.mints.at(-1)));
  // The catalog no longer offers this pool (paused or unsupported); the server refuses it as well.
  const poolBlocking = p.submitted ? null : openableReason(p.pool);
  return (
    <form
      id="open-position-form"
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (
          !viewer &&
          (!expired || recenterReplay) &&
          p.executorReady &&
          !(p.autopilotBlocking && !p.submitted) &&
          poolBlocking === null
        )
          void p.onSubmit(event);
      }}
    >
      {p.headerTarget &&
        createPortal(
          <span
            className={`quote-seal${expired ? " expired" : ""}`}
            title={
              Number.isFinite(expiresAt)
                ? `Valid until ${new Date(expiresAt).toLocaleTimeString()}`
                : undefined
            }
          >
            <span>{expired ? "Quote expired" : "Quote valid"}</span>
            <span className="num">
              {expired
                ? "—"
                : `${String(Math.floor(remainingSeconds / 60))}:${String(remainingSeconds % 60).padStart(2, "0")}`}
            </span>
          </span>,
          p.headerTarget,
        )}
      {p.quote.recenterReview && (
        <section className="order-composition" aria-label="Recentered range review">
          <p>
            Original range: {p.quote.recenterReview.belowPercent}% below and{" "}
            {p.quote.recenterReview.abovePercent}% above, in the original{" "}
            {p.quote.recenterReview.originalRange.inverted ? "inverse" : "pool"} price orientation.
          </p>
          <p>
            Latest reference: {formatPrice(p.quote.recenterReview.referencePrice)}{" "}
            {frame.unitOf(p.quote.recenterReview.baseMint, p.quote.recenterReview.quoteMint)} ·
            observed {new Date(p.quote.recenterReview.observedAt).toLocaleTimeString()}
          </p>
          <p className="hint">
            Native funding and other limits are unchanged. This is a new operation with a fresh
            attempt and fee budget. Stop percentages use{" "}
            {priceFrame(p.pool.tokenA, p.pool.tokenB, !!p.quote.exitPolicy.thresholdInverted).unit}.
          </p>
        </section>
      )}
      <dl className="quote-summary" aria-label="Stored quote summary">
        <div>
          <dt>Range</dt>
          <dd>
            <span className="num">
              {formatPrice(lower)} – {formatPrice(upper)} {p.unit}
            </span>
            <small>Tick-rounded</small>
          </dd>
        </div>
        <div>
          <dt>You deposit</dt>
          <dd>
            <span className="num">
              {p.quote.composition
                ? `${compositionAmount(p.quote.composition.depositA)} + ${compositionAmount(p.quote.composition.depositB)}`
                : "— · Composition not provided"}
            </span>
            <small>
              {usdValue(p.quote.composition?.totalDepositUsd ?? null)} · lower estimate{" "}
              {usdValue(p.quote.composition?.minimumTotalDepositUsd ?? null)}
            </small>
            <small>
              {p.quote.composition?.minimumDepositA && p.quote.composition.minimumDepositB
                ? `Lower amounts: ${compositionAmount(p.quote.composition.minimumDepositA)} + ${compositionAmount(p.quote.composition.minimumDepositB)}`
                : "Lower token amounts — · not provided"}
            </small>
          </dd>
        </div>
        <div>
          <dt>From wallet</dt>
          <dd>
            <span className="num">
              {nativeAmount(p.request.amountNative, p.request.fundingMint)}
            </span>
            <small>{p.usd ? `≈ ${usdValue(p.usd)}` : usdValue(p.usd)}</small>
          </dd>
        </div>
        <div>
          <dt>Swap</dt>
          <dd>
            {p.quote.routes.length ? (
              p.quote.routes.map((route) => (
                <span className="num" key={`${route.inputNative}:${route.mints.join(":")}`}>
                  {nativeAmount(route.inputNative, route.mints.at(0))} → ≥{" "}
                  {nativeAmount(route.minimumOutputNative, route.mints.at(-1))}
                </span>
              ))
            ) : (
              <span>No swap required</span>
            )}
            <small>
              {routed?.route ?? `${dexName(p.pool.dex)} pools only`} · slippage ≤{" "}
              {formatNative(BigInt(p.quote.caps.maxSlippageBps), 2)}%
              {p.quote.composition?.swap
                ? ` · value change ${usdValue(p.quote.composition.swap.valueDifferenceUsd)}`
                : ""}
            </small>
            {routed?.recovery && <small>{routed.recovery}</small>}
          </dd>
        </div>
        <div>
          <dt>Auto-exit</dt>
          <dd>
            <span>
              {p.quote.exitPolicy.enabled ? stopPairText(displayStopPair) : "Disabled"} →{" "}
              {poolToken(p.pool, p.quote.exitPolicy.targetMint)?.symbol ??
                shorten(p.quote.exitPolicy.targetMint)}
            </span>
            {p.quote.exitPolicy.enabled && (
              <small>
                Stops {lowerStop ? formatPrice(lowerStop) : "—"} ·{" "}
                {upperStop ? formatPrice(upperStop) : "—"} {p.unit}
              </small>
            )}
          </dd>
        </div>
        <div>
          <dt>Limits</dt>
          <dd>
            <span className="num">
              network ≤ {formatNative(BigInt(p.quote.caps.maxNetworkFeeLamports), 9)} · rent ≤{" "}
              {formatNative(BigInt(p.quote.caps.maxRentLamports), 9)} SOL
            </span>
            <small>
              priority ≤ {formatNative(BigInt(p.quote.caps.maxPriorityFeeLamports), 9)} · reserve ≥{" "}
              {formatNative(BigInt(p.quote.caps.minSolReserveLamports), 9)} SOL
            </small>
            <small>
              spend ≤ {nativeAmount(p.quote.caps.maxInputNative, p.request.fundingMint)} · impact ≤{" "}
              {formatNative(BigInt(p.quote.caps.maxPriceImpactBps), 2)}% ·{" "}
              {p.quote.caps.maxAttempts} attempts
            </small>
          </dd>
        </div>
      </dl>
      <ScaledPoolUnitsNote tokens={p.scaledTokens} />
      <details className="review-audit">
        <summary>Details · Exact native amounts, ticks and intent hash</summary>
        <FundingSummary
          amount={p.amount}
          usd={p.usd}
          symbol={poolToken(p.pool, p.request.fundingMint)?.symbol ?? p.request.fundingMint}
          observedAt={p.funding.observedAt}
          pending={false}
        />
        {p.quote.composition && (
          <>
            <PositionPreview
              composition={p.quote.composition}
              pool={p.pool}
              caps={p.quote.caps}
              frame={frame}
            />
            <CompositionMinimums composition={p.quote.composition} pool={p.pool} />
          </>
        )}
        <PriceScale
          min={lower}
          max={upper}
          current={p.reference}
          lowerStop={lowerStop}
          upperStop={upperStop}
          unit={p.unit}
        />
        <p className="hint">
          {p.quote.exitPolicy.enabled
            ? `Automatic exit: ${stopPairText(displayStopPair)} (${p.unit}).`
            : "Automatic exit disabled."}
        </p>
        <p className="hint">
          Return token:{" "}
          {poolToken(p.pool, p.quote.exitPolicy.targetMint)?.symbol ??
            shorten(p.quote.exitPolicy.targetMint)}
        </p>
        <section aria-label="Exact quote, routes and execution limits">
          <ReadonlyValue label="intent hash" value={p.quote.hash} />
          <dl className="review-grid">
            <div>
              <dt>Pool and ticks</dt>
              <dd>
                {frame.pair} · {formatPercent(p.pool.feePercent)} · {p.quote.lowerTick}…
                {p.quote.upperTick}
              </dd>
            </div>
            <div>
              <dt>Maximum spend</dt>
              <dd className="amount-line scroll-value">
                {p.quote.caps.maxInputNative} native units
              </dd>
            </div>
            <div>
              <dt>Route</dt>
              <dd>
                {p.quote.routes.length === 0
                  ? "No swap required"
                  : p.quote.routes.map((route) => {
                      const input = poolToken(p.pool, route.mints.at(0));
                      const output = poolToken(p.pool, route.mints.at(-1));
                      return (
                        <div
                          className="route-details"
                          key={`${route.inputNative}:${route.mints.join(":")}`}
                        >
                          <span>
                            {route.mints
                              .map((mint) => poolToken(p.pool, mint)?.symbol ?? shorten(mint))
                              .join(" → ")}
                          </span>
                          <span className="amount-line scroll-value">
                            Input: {routeAmount(route.inputNative, input)}
                          </span>
                          <span className="amount-line scroll-value">
                            Minimum swap output: {routeAmount(route.minimumOutputNative, output)}
                          </span>
                          <span className="amount-line scroll-value">
                            Exact input: {route.inputNative} native units
                          </span>
                          <span className="amount-line scroll-value">
                            Exact minimum output: {route.minimumOutputNative} native units
                          </span>
                        </div>
                      );
                    })}
              </dd>
            </div>
            <div>
              <dt>Network / priority / rent caps</dt>
              <dd>
                {formatNative(BigInt(p.quote.caps.maxNetworkFeeLamports), 9)} /{" "}
                {formatNative(BigInt(p.quote.caps.maxPriorityFeeLamports), 9)} /{" "}
                {formatNative(BigInt(p.quote.caps.maxRentLamports), 9)} SOL
              </dd>
            </div>
            <div>
              <dt>SOL reserve</dt>
              <dd>at least {formatNative(BigInt(p.quote.caps.minSolReserveLamports), 9)} SOL</dd>
            </div>
            <div>
              <dt>Tolerances and retries</dt>
              <dd>
                {p.quote.caps.maxSlippageBps} bps slippage · {p.quote.caps.maxPriceImpactBps} bps
                impact · {p.quote.caps.maxAttempts} attempts
              </dd>
            </div>
          </dl>
        </section>
        <section className="minimum-output-summary" aria-label="Minimum swap output">
          {p.quote.routes.length === 0 ? (
            <strong>No swap is required, so no minimum swap output applies.</strong>
          ) : (
            p.quote.routes.map((route) => (
              <strong className="amount-line scroll-value" key={route.minimumOutputNative}>
                Minimum swap output:{" "}
                {routeAmount(route.minimumOutputNative, poolToken(p.pool, route.mints.at(-1)))}
              </strong>
            ))
          )}
          <small>
            This is the minimum output accepted from the swap, not a minimum amount deposited into
            the pool.
          </small>
        </section>
      </details>
      {p.executorState !== "ready" && (
        <p className="inline-alert warning" role="status">
          Executor {p.executorState}. The quote can be reviewed, but the financial command is
          blocked.
        </p>
      )}
      {p.autopilotBlocking && !p.submitted && (
        <p className="inline-alert warning" role="status">
          {AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON} The quote can still be reviewed.
        </p>
      )}
      {poolBlocking !== null && (
        <p className="inline-alert warning" role="status">
          {poolBlocking}. Opening is unavailable for this pool.
        </p>
      )}
      {p.pending ? (
        <div className="operation-message" role="status">
          <span className="spinner" aria-hidden="true" />
          <div>
            <strong>Submitting opening…</strong>
            <p>Checking whether the opening was accepted.</p>
          </div>
        </div>
      ) : p.submitted ? (
        <div className="inline-alert warning" role="status">
          <div>
            <strong>Submission outcome is uncertain</strong>
            <p>
              The request may have been accepted even though no answer arrived. Check before
              starting another opening.
            </p>
            <p>
              Check again replays the same quote and request key. It may submit the original opening
              if the first request never arrived.
            </p>
            {((expired && !recenterReplay) || !p.executorReady) && (
              <p>
                {expired
                  ? "The submitted quote expired. Check My positions for the recorded result."
                  : !p.identityMatches
                    ? "The wallet or opening context changed. Check My positions for the recorded result."
                    : "Executor unavailable. Check again is disabled until execution is ready."}
              </p>
            )}
            {p.error && (
              <details>
                <summary>Details</summary>
                {p.error}
              </details>
            )}
          </div>
        </div>
      ) : (
        <>
          {expired && (
            <div className="inline-alert warning" role="status">
              <div>
                <strong>Quote expired</strong>
                <p>Request a new quote before confirming. Your inputs are kept.</p>
              </div>
            </div>
          )}
          {p.error && (
            <p className="inline-alert warning" role="alert">
              {p.error}
            </p>
          )}
          <p className="review-approval">
            {viewer
              ? "Read-only session: this quote can be reviewed, but not confirmed."
              : "Confirming approves exactly these limits. Leaving after acceptance does not cancel the position opening."}
          </p>
        </>
      )}
      {p.actionTarget &&
        !viewer &&
        createPortal(
          <button
            type="submit"
            form="open-position-form"
            className="button primary"
            title={
              p.autopilotBlocking && !p.submitted
                ? AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON
                : (poolBlocking ?? undefined)
            }
            disabled={
              p.pending ||
              !p.executorReady ||
              (expired && !recenterReplay) ||
              (p.autopilotBlocking && !p.submitted) ||
              poolBlocking !== null
            }
          >
            {p.pending ? "Submitting opening…" : p.submitted ? "Check again" : "Confirm and open"}
          </button>,
          p.actionTarget,
        )}
    </form>
  );
}

function poolToken(pool: Pool, mint: string | undefined) {
  return [pool.tokenA, pool.tokenB].find((token) => token.mint === mint);
}

function routeAmount(value: string, token: Token | undefined) {
  if (!token) return `${value} native units`;
  try {
    return `${formatToken(formatNative(BigInt(value), token.decimals), "long")} ${token.symbol}`;
  } catch {
    return `Unavailable (${token.symbol})`;
  }
}

function ReadonlyValue({ label, value }: { label: string; value: string }) {
  return (
    <label className="readonly-value">
      <span>{label}</span>
      <input aria-label={`Exact ${label}`} readOnly value={value} />
    </label>
  );
}
